import './VolumeCurveChart.scss';
import { VolumeCurveData } from 'widgets/volumeCurve/data/volumeCurveData.ts';
import { useWidgetContext } from 'widgets/WidgetContext.tsx';
import { useYScale } from 'utils/components/TimeSeriesChart/yAxis.ts';
import { computeYDomain, getYTicks, getChartSeries } from './series';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart.tsx';
import AreaGradient from 'utils/components/TimeSeriesChart/AreaGradient.tsx';
import React from 'react';
import { useVolumeCurveChartData } from 'widgets/volumeCurve/components/chart/chartData.ts';
import { postfixNumber } from '@instinet-analytics/core-lib';
import { useVolumeCurveDimensions } from 'widgets/volumeCurve/components/dimensions.ts';
import { VolumeCurveChartLegends } from 'widgets/volumeCurve/components/chart/VolumeCurveChartLegends.tsx';
import { useVolumeCurveConfigSlice } from 'widgets/volumeCurve/store/configuration/configSlice.ts';
import { VolumeCurveConfiguration } from 'widgets/volumeCurve/store/configuration/VolumeCurveConfiguration.ts';
import classnames from 'classnames';
import classNames from 'classnames';

export default function VolumeCurveChart({ data }: { data: VolumeCurveData }) {
    const { widgetKey } = useWidgetContext();

    const { bodyHeight, width, chartHeight } = useVolumeCurveDimensions();

    const chartData = useVolumeCurveChartData(data);
    const { config } = useVolumeCurveConfigSlice();

    // eslint-disable-next-line no-console
    console.log('VolumeCurveChart', { chartData, data });

    const cumulativeHistoricalGradientId = `cumulativeHistoricalGradientId-${widgetKey}`;
    const cumulativeTradedGradientId = `cumulativeTradedGradientId-${widgetKey}`;

    const yDomain = computeYDomain(chartData.bucketedData ?? [], config);
    const yScale = useYScale(yDomain, [0, 1]);

    const hideSeriesClasses = getHideSeriesClasses(config);

    const series = getChartSeries(
        chartData,
        width,
        cumulativeHistoricalGradientId,
        cumulativeTradedGradientId,
        yDomain
    );
    const yTicks = getYTicks();

    const cssClasses = classNames('VolumeCurveChart', hideSeriesClasses);

    return (
        <div
            className={cssClasses}
            style={{
                height: bodyHeight + 'px',
                width: width + 'px',
            }}
        >
            <VolumeCurveChartLegends
                containerLeft={0}
                data={chartData.bucketedData}
            />
            <TimeSeriesChart
                id="VolumeCurveChart"
                className="VolumeCurveChart-chart"
                height={chartHeight}
                data={chartData.bucketedData}
                series={series}
                displayXAxis={true}
                yScale={yScale}
                yTicks={yTicks}
                yTickFormat={postfixNumber}
            />
            <AreaGradient
                identifier={cumulativeHistoricalGradientId}
                className="cumulativeHistoricalGradient"
                startOffset="0%"
                stopOffset="100%"
            />
            <AreaGradient
                identifier={cumulativeTradedGradientId}
                className="cumulativeTradedGradient"
                startOffset="0%"
                stopOffset="100%"
            />
        </div>
    );
}

export function getHideSeriesClasses(config: VolumeCurveConfiguration) {
    const {
        hideTradedVolume,
        hideHistoricalVolumeExcludingAuction,
        hideAuctionVolume,
        hideCumulativeHistoricalVolume,
        hideCumulativeTradedVolume,
    } = config;

    return classnames(
        hideTradedVolume && 'hide-traded-bars',
        hideHistoricalVolumeExcludingAuction && 'hide-historical-bars',
        hideAuctionVolume && 'hide-auction-bars',
        hideCumulativeHistoricalVolume && 'hide-cum-hist',
        hideCumulativeTradedVolume && 'hide-cum-traded'
    );
}
